import { LOGROCKET_PERF_EVENTS, LOGROCKET_PERF_SESSION_KEYS } from '@aca-new/app/shared/constants/logrocket-performance.constants';
import { Injectable } from '@angular/core';
import logRocket from 'logrocket';

const PERFORMANCE_START_TIME_KEY = 'performanceStartTime';

export type LogRocketPerfProps = Record<string, string | number | boolean | undefined>;

/**
 * Wraps LogRocket custom events for performance timing (paired start/end with correlationId).
 */
@Injectable({
  providedIn: 'root',
})
export class LogRocketPerformanceService {
  /**
   * @returns {string} A new UUID suitable for correlating start/end custom events.
   */
  public newCorrelationId(): string {
    if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
      return crypto.randomUUID();
    }

    return `${Date.now()}-${Math.random().toString(36).slice(2, 11)}`;
  }

  /**
   * @param {string} eventName LogRocket custom event name.
   * @param {LogRocketPerfProps | undefined} props Optional scalar properties (no nested objects).
   * @returns {void} Nothing.
   */
  public track(eventName: string, props?: LogRocketPerfProps): void {
    try {
      const cleaned: Record<string, string | number | boolean> | undefined = props ? this._cleanProps(props) : undefined;

      if (cleaned && Object.keys(cleaned).length > 0) {
        logRocket.track(eventName, cleaned);
      } else {
        logRocket.track(eventName);
      }
    } catch {
      // LogRocket may be disabled (e.g. test company) or not yet initialised.
    }
  }

  /**
   * Persists flow metadata and emits the start event.
   * Clears any existing pending payload for `storageKey` first so a failed prior `setItem` cannot leave a stale row.
   * @param {string} storageKey sessionStorage key from LOGROCKET_PERF_SESSION_KEYS.
   * @param {string} startEvent LogRocket event name for the start of the flow.
   * @param {LogRocketPerfProps} props Properties stored and sent with the start event.
   * @returns {string} correlationId for optional local use.
   */
  public beginWithStorage(storageKey: string, startEvent: string, props: LogRocketPerfProps): string {
    const correlationId: string = this.newCorrelationId();
    const snapshot: LogRocketPerfProps = {
      ...props,
      correlationId,
      [PERFORMANCE_START_TIME_KEY]: this._getPerformanceNow(),
    };

    this.cancelPendingFlow(storageKey);
    this._safeSessionSet(storageKey, JSON.stringify(this._cleanProps(snapshot)));
    this.track(startEvent, this._stripInternalProps(snapshot));

    return correlationId;
  }

  /**
   * Reads pending flow metadata, clears storage, and emits the end event.
   * @param {string} storageKey sessionStorage key used in beginWithStorage.
   * @param {string} endEvent LogRocket event name for the end of the flow.
   * @param {LogRocketPerfProps | undefined} extraProps Optional extra properties merged into the end event.
   * @returns {void} Nothing.
   */
  public endFromStorage(storageKey: string, endEvent: string, extraProps?: LogRocketPerfProps): void {
    const raw: string | null = this._safeSessionGet(storageKey);

    if (!raw) {
      return;
    }

    this._safeSessionRemove(storageKey);

    try {
      const parsed: LogRocketPerfProps = JSON.parse(raw) as LogRocketPerfProps;
      const performanceTime: number | undefined = this._calculatePerformanceTime(parsed[PERFORMANCE_START_TIME_KEY]);
      const merged: LogRocketPerfProps = {
        ...this._stripInternalProps(parsed),
        performanceTime,
        ...this._cleanProps(extraProps ?? {}),
      };

      this.track(endEvent, merged);
    } catch {
      // Invalid JSON in sessionStorage — ignore.
    }
  }

  /**
   * Removes a pending flow without emitting an event (e.g. HTTP error before completion).
   * @param {string} storageKey sessionStorage key from LOGROCKET_PERF_SESSION_KEYS.
   * @returns {void} Nothing.
   */
  public cancelPendingFlow(storageKey: string): void {
    this._safeSessionRemove(storageKey);
  }

  /**
   * Reads pending flow JSON without removing it. Values are the same scalar shapes as
   * {@link LogRocketPerformanceService.beginWithStorage} (string, number, or boolean per key, e.g. `productIndex`).
   * @param {string} storageKey sessionStorage key to read without removing.
   * @returns {LogRocketPerfProps | null} Parsed object payload or null if missing, non-object JSON, invalid JSON, or storage unavailable.
   */
  public readPendingJson(storageKey: string): LogRocketPerfProps | null {
    const raw: string | null = this._safeSessionGet(storageKey);

    if (!raw) {
      return null;
    }

    try {
      const value: unknown = JSON.parse(raw);

      if (value === null || typeof value !== 'object' || Array.isArray(value)) {
        return null;
      }

      return value as LogRocketPerfProps;
    } catch {
      return null;
    }
  }

  /**
   * Ends the view-order flow only when the loaded order matches the pending correlation payload.
   * @param {string} orderId Order id from the route or API.
   * @returns {void} Nothing.
   */
  public endViewOrderIfMatches(orderId: string): void {
    const pending: LogRocketPerfProps | null = this.readPendingJson(LOGROCKET_PERF_SESSION_KEYS.viewOrder);
    const pendingOrderId: string | number | boolean | undefined = pending?.orderId;

    if (pendingOrderId === undefined || pendingOrderId === null || typeof pendingOrderId === 'boolean') {
      return;
    }

    const pendingOrderIdStr: string = typeof pendingOrderId === 'string' ? pendingOrderId : pendingOrderId.toString();

    if (pendingOrderIdStr !== orderId) {
      return;
    }

    this.endFromStorage(LOGROCKET_PERF_SESSION_KEYS.viewOrder, LOGROCKET_PERF_EVENTS.openBookingsViewOrderLoaded, {
      orderId,
    });
  }

  /**
   * @param {string} key sessionStorage key.
   * @returns {string | null} Stored value or null when unavailable or on access errors.
   */
  private _safeSessionGet(key: string): string | null {
    try {
      if (typeof sessionStorage === 'undefined') {
        return null;
      }

      return sessionStorage.getItem(key);
    } catch {
      return null;
    }
  }

  /**
   * @param {string} key sessionStorage key.
   * @param {string} value JSON string to store.
   * @returns {void} Nothing.
   */
  private _safeSessionSet(key: string, value: string): void {
    try {
      if (typeof sessionStorage === 'undefined') {
        return;
      }

      sessionStorage.setItem(key, value);
    } catch {
      // Storage may be blocked, full, or throw in embedded / privacy contexts.
    }
  }

  /**
   * @param {string} key sessionStorage key.
   * @returns {void} Nothing.
   */
  private _safeSessionRemove(key: string): void {
    try {
      if (typeof sessionStorage === 'undefined') {
        return;
      }

      sessionStorage.removeItem(key);
    } catch {
      // Same rationale as _safeSessionSet.
    }
  }

  /**
   * @returns {number} High-resolution timestamp, or epoch time when Performance API is unavailable.
   */
  private _getPerformanceNow(): number {
    if (typeof performance !== 'undefined' && typeof performance.now === 'function') {
      const performanceNow: number = performance.now();
      const performanceTimeOrigin: number = typeof performance.timeOrigin === 'number' ? performance.timeOrigin : Date.now() - performanceNow;

      return performanceTimeOrigin + performanceNow;
    }

    return Date.now();
  }

  /**
   * @param {LogRocketPerfProps[string]} performanceStartTime Stored start timestamp from the pending flow.
   * @returns {number | undefined} Rounded elapsed time in milliseconds, or undefined when no start time exists.
   */
  private _calculatePerformanceTime(performanceStartTime: LogRocketPerfProps[string]): number | undefined {
    if (typeof performanceStartTime !== 'number') {
      return undefined;
    }

    return Math.max(0, Math.round(this._getPerformanceNow() - performanceStartTime));
  }

  /**
   * @param {LogRocketPerfProps} props Stored flow payload.
   * @returns {LogRocketPerfProps} Payload without service-internal timing metadata.
   */
  private _stripInternalProps(props: LogRocketPerfProps): LogRocketPerfProps {
    const publicProps: LogRocketPerfProps = { ...props };

    delete publicProps[PERFORMANCE_START_TIME_KEY];

    return publicProps;
  }

  /**
   * @param {LogRocketPerfProps} props Raw event payload.
   * @returns {Record<string, string | number | boolean>} Scalar payload accepted by LogRocket.
   */
  private _cleanProps(props: LogRocketPerfProps): Record<string, string | number | boolean> {
    const out: Record<string, string | number | boolean> = {};

    for (const [key, value] of Object.entries(props)) {
      if (value === undefined || value === '') {
        continue;
      }

      out[key] = value;
    }

    return out;
  }
}
